import React, { useState, useEffect, useMemo } from 'react';
import { speciesAPI } from '../services/api';
import { toast } from 'react-toastify';

const PHOTO_BASE = window.location.origin;
const getPhotoUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  return `${PHOTO_BASE}${url}`;
};

export default function SpeciesReport() {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [selectedSpecies, setSelectedSpecies] = useState(null);

  // Filters
  const [projectFilter, setProjectFilter] = useState(''); // target_id or ''
  const [zoneFilter, setZoneFilter] = useState('');       // zone_label or ''
  const [familyFilter, setFamilyFilter] = useState('');   // family or ''

  // Per-project data (loaded when a specific project is picked)
  const [projectDetail, setProjectDetail] = useState(null); // { target, zones, species_totals }
  const [projectLoading, setProjectLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    speciesAPI.report()
      .then(res => setReport(res.data))
      .catch(() => toast.error('Failed to load species report'))
      .finally(() => setLoading(false));
  }, []);

  // When project filter changes, fetch project detail for zone-level info
  useEffect(() => {
    if (!projectFilter) {
      setProjectDetail(null);
      setZoneFilter('');
      return;
    }
    setProjectLoading(true);
    speciesAPI.targetSpecies(projectFilter)
      .then(res => setProjectDetail(res.data))
      .catch(() => toast.error('Failed to load project species'))
      .finally(() => setProjectLoading(false));
  }, [projectFilter]);

  // Build project list from the report response
  const allProjects = useMemo(() => {
    if (!report) return [];
    const seen = new Map();
    report.species.forEach(sp => {
      (sp.projects || []).forEach(p => {
        if (!seen.has(p.target_id)) {
          seen.set(p.target_id, { id: p.target_id, title: p.title, city: p.city, state: p.state });
        }
      });
    });
    return Array.from(seen.values()).sort((a, b) => (a.title || '').localeCompare(b.title || ''));
  }, [report]);

  // Family list from full report
  const allFamilies = useMemo(() => {
    if (!report) return [];
    const set = new Set();
    report.species.forEach(sp => { if (sp.family) set.add(sp.family); });
    return Array.from(set).sort();
  }, [report]);

  // Zones available when a project is selected
  const zonesForProject = useMemo(() => {
    if (!projectDetail) return [];
    return projectDetail.zones.map(z => z.zone_label);
  }, [projectDetail]);

  // Compute the species list to display based on all filters
  const displaySpecies = useMemo(() => {
    if (!report) return [];

    // Base list: if project filter set → use project's species_totals; else use full report
    let list;
    if (projectFilter && projectDetail) {
      // If zone also filtered → get species from that zone only
      if (zoneFilter) {
        const zone = projectDetail.zones.find(z => z.zone_label === zoneFilter);
        list = zone ? zone.items.map(item => ({
          ...item.species,
          total_quantity: item.quantity,
          projects: [{
            target_id: projectDetail.target.id,
            title: projectDetail.target.title,
            city: projectDetail.target.city,
            state: projectDetail.target.state,
            quantity: item.quantity,
          }],
        })) : [];
      } else {
        // whole project — use species_totals + inject synthetic projects list
        list = (projectDetail.species_totals || []).map(sp => ({
          ...sp,
          projects: [{
            target_id: projectDetail.target.id,
            title: projectDetail.target.title,
            city: projectDetail.target.city,
            state: projectDetail.target.state,
            quantity: sp.total_quantity,
          }],
        }));
      }
    } else {
      list = report.species;
    }

    // Apply family filter
    if (familyFilter) {
      list = list.filter(sp => sp.family === familyFilter);
    }

    // Apply text search
    if (q) {
      const s = q.toLowerCase();
      list = list.filter(sp =>
        (sp.common_name && sp.common_name.toLowerCase().includes(s)) ||
        (sp.local_name && sp.local_name.toLowerCase().includes(s)) ||
        (sp.scientific_name && sp.scientific_name.toLowerCase().includes(s)) ||
        (sp.family && sp.family.toLowerCase().includes(s))
      );
    }

    return list.sort((a, b) => (b.total_quantity || 0) - (a.total_quantity || 0));
  }, [report, projectDetail, projectFilter, zoneFilter, familyFilter, q]);

  // Family breakdown for currently displayed set
  const familyBreakdown = useMemo(() => {
    const map = {};
    displaySpecies.forEach(sp => {
      const fam = sp.family || 'Unknown';
      map[fam] = (map[fam] || 0) + (sp.total_quantity || 0);
    });
    return Object.entries(map).map(([family, total]) => ({ family, total })).sort((a, b) => b.total - a.total);
  }, [displaySpecies]);

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>Loading species report...</div>;
  if (!report) return <div style={{ padding: 40, textAlign: 'center' }}>No data.</div>;

  const grandTotal = displaySpecies.reduce((s, sp) => s + (sp.total_quantity || 0), 0);
  const activeFilters = [projectFilter, zoneFilter, familyFilter, q].filter(Boolean).length;

  const selStyle = { padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13, background: '#fff', minWidth: 160, maxWidth: '100%' };

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <h2 style={{ margin: 0, color: '#1b5e20' }}>🌿 Species Report</h2>
        <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
          {displaySpecies.length} species · {grandTotal.toLocaleString()} plants
          {activeFilters > 0 && (
            <span style={{ marginLeft: 8, background: '#dcfce7', color: '#166534', padding: '2px 8px', borderRadius: 10, fontSize: 11 }}>
              {activeFilters} filter{activeFilters > 1 ? 's' : ''} active
            </span>
          )}
        </div>
      </div>

      {/* Filter bar */}
      <div style={{
        background: '#fff', borderRadius: 12, padding: 14, marginBottom: 16,
        boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
        display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center',
      }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <label style={{ fontSize: 11, fontWeight: 600, color: '#6b7280' }}>PROJECT</label>
          <select value={projectFilter} onChange={e => { setProjectFilter(e.target.value); setZoneFilter(''); }} style={selStyle}>
            <option value="">All Projects ({allProjects.length})</option>
            {allProjects.map(p => (
              <option key={p.id} value={p.id}>{p.title}{p.city ? ` — ${p.city}` : ''}</option>
            ))}
          </select>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <label style={{ fontSize: 11, fontWeight: 600, color: '#6b7280' }}>ZONE / PLOT</label>
          <select value={zoneFilter} onChange={e => setZoneFilter(e.target.value)} style={{ ...selStyle, opacity: projectFilter ? 1 : 0.5 }} disabled={!projectFilter || projectLoading}>
            <option value="">
              {!projectFilter ? 'Select project first' : projectLoading ? 'Loading…' : `All zones (${zonesForProject.length})`}
            </option>
            {zonesForProject.map(z => (
              <option key={z} value={z}>{z}</option>
            ))}
          </select>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <label style={{ fontSize: 11, fontWeight: 600, color: '#6b7280' }}>FAMILY</label>
          <select value={familyFilter} onChange={e => setFamilyFilter(e.target.value)} style={selStyle}>
            <option value="">All Families ({allFamilies.length})</option>
            {allFamilies.map(f => (
              <option key={f} value={f}>{f}</option>
            ))}
          </select>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 3, flex: 1, minWidth: 200 }}>
          <label style={{ fontSize: 11, fontWeight: 600, color: '#6b7280' }}>SEARCH</label>
          <input type="search" placeholder="Search name / scientific / family..." value={q} onChange={e => setQ(e.target.value)}
            style={{ padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13, width: '100%' }} />
        </div>

        {activeFilters > 0 && (
          <button
            onClick={() => { setProjectFilter(''); setZoneFilter(''); setFamilyFilter(''); setQ(''); }}
            style={{ padding: '9px 14px', background: '#ef4444', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 600, alignSelf: 'flex-end' }}
          >
            ✕ Clear Filters
          </button>
        )}
      </div>

      {/* Family breakdown (filtered) */}
      {familyBreakdown.length > 0 && (
        <div style={{ background: '#fff', borderRadius: 12, padding: 20, marginBottom: 16, boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: '#1a1a2e', marginBottom: 12 }}>By Botanical Family</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 10 }}>
            {familyBreakdown.slice(0, 12).map(f => (
              <div key={f.family}
                onClick={() => setFamilyFilter(familyFilter === f.family ? '' : f.family)}
                style={{
                  background: familyFilter === f.family ? '#dcfce7' : '#f0fdf4',
                  borderRadius: 8, padding: '10px 14px',
                  borderLeft: `4px solid ${familyFilter === f.family ? '#166534' : '#16a34a'}`,
                  cursor: 'pointer',
                }}>
                <div style={{ fontSize: 12, color: '#6b7280', fontWeight: 500, wordBreak: 'break-word' }}>{f.family}</div>
                <div style={{ fontSize: 18, fontWeight: 700, color: '#1b5e20' }}>{f.total.toLocaleString()}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Species grid */}
      <div style={{ background: '#fff', borderRadius: 12, padding: 20, boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: '#1a1a2e', marginBottom: 12 }}>Species</div>
        {displaySpecies.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>No species match current filters</div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 14 }}>
            {displaySpecies.map(sp => (
              <div key={sp.id}
                onClick={() => setSelectedSpecies(sp)}
                style={{
                  background: '#f9fafb', borderRadius: 12, padding: 14, cursor: 'pointer',
                  border: '1px solid #e5e7eb', transition: 'all 0.15s', minWidth: 0,
                }}
                onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#16a34a'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(22,163,74,0.15)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#e5e7eb'; e.currentTarget.style.boxShadow = 'none'; }}
              >
                <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                  {sp.image_url ? (
                    <img src={getPhotoUrl(sp.image_url)} alt={sp.common_name}
                      style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 8, flexShrink: 0 }}
                      onError={(e) => { e.target.style.display = 'none'; }} />
                  ) : (
                    <div style={{ width: 80, height: 80, background: '#e5e7eb', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 32, flexShrink: 0 }}>🌱</div>
                  )}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: '#1a1a2e', wordBreak: 'break-word' }}>{sp.common_name}</div>
                    {sp.local_name && <div style={{ fontSize: 12, color: '#6b7280', wordBreak: 'break-word' }}>{sp.local_name}</div>}
                    {sp.scientific_name && <div style={{ fontSize: 11, color: '#374151', fontStyle: 'italic', marginTop: 2, wordBreak: 'break-word' }}>{sp.scientific_name}</div>}
                    {sp.family && <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>{sp.family}</div>}
                    <div style={{ display: 'flex', gap: 6, marginTop: 6, flexWrap: 'wrap' }}>
                      <span style={{ background: '#dcfce7', color: '#166534', padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 700 }}>
                        {(sp.total_quantity || 0).toLocaleString()} plants
                      </span>
                      {sp.projects && (
                        <span style={{ background: '#dbeafe', color: '#1e40af', padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 500 }}>
                          {sp.projects.length} project{sp.projects.length !== 1 ? 's' : ''}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Species details modal */}
      {selectedSpecies && (
        <div onClick={() => setSelectedSpecies(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 14, padding: 24, maxWidth: 640, width: '100%', maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', gap: 20, marginBottom: 20, flexWrap: 'wrap' }}>
              {selectedSpecies.image_url ? (
                <img src={getPhotoUrl(selectedSpecies.image_url)} alt={selectedSpecies.common_name}
                  style={{ width: 180, height: 180, objectFit: 'cover', borderRadius: 12 }}
                  onError={(e) => { e.target.style.display = 'none'; }} />
              ) : (
                <div style={{ width: 180, height: 180, background: '#e5e7eb', borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 64 }}>🌱</div>
              )}
              <div style={{ flex: 1, minWidth: 200 }}>
                <h3 style={{ margin: '0 0 4px', color: '#1b5e20', fontSize: 22, wordBreak: 'break-word' }}>{selectedSpecies.common_name}</h3>
                {selectedSpecies.local_name && <div style={{ fontSize: 15, color: '#6b7280', marginBottom: 4 }}>{selectedSpecies.local_name}</div>}
                {selectedSpecies.scientific_name && <div style={{ fontSize: 14, fontStyle: 'italic', color: '#374151', marginBottom: 4 }}>{selectedSpecies.scientific_name}</div>}
                {selectedSpecies.family && <div style={{ fontSize: 13, color: '#9ca3af', marginBottom: 8 }}>Family: {selectedSpecies.family}</div>}
                <div style={{ fontSize: 24, fontWeight: 800, color: '#1a7431', marginTop: 8 }}>
                  {(selectedSpecies.total_quantity || 0).toLocaleString()} <span style={{ fontSize: 14, fontWeight: 500, color: '#6b7280' }}>plants total</span>
                </div>
              </div>
            </div>

            {selectedSpecies.description && (
              <div style={{ marginBottom: 16, fontSize: 13, color: '#374151', lineHeight: 1.5 }}>
                {selectedSpecies.description}
              </div>
            )}

            {selectedSpecies.projects && selectedSpecies.projects.length > 0 && (
              <>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#1a1a2e', marginBottom: 8 }}>Projects using this species:</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {selectedSpecies.projects.map((p, i) => (
                    <div key={i} style={{ background: '#f9fafb', padding: '8px 12px', borderRadius: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: '#1a1a2e', wordBreak: 'break-word' }}>{p.title}</div>
                        {(p.city || p.state) && <div style={{ fontSize: 11, color: '#6b7280' }}>{[p.city, p.state].filter(Boolean).join(', ')}</div>}
                      </div>
                      <div style={{ fontSize: 14, fontWeight: 700, color: '#1a7431', flexShrink: 0 }}>{p.quantity.toLocaleString()}</div>
                    </div>
                  ))}
                </div>
              </>
            )}

            <div style={{ marginTop: 16, textAlign: 'right' }}>
              <button onClick={() => setSelectedSpecies(null)} style={{ padding: '8px 16px', background: '#1a7431', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600 }}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
